import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ViewportProvider } from './contexts/ViewportContext';
import { ConnectionStateProvider } from './contexts/ConnectionStateContext';
import { KeyboardShortcutsProvider } from './contexts/KeyboardShortcutsContext';
import { AppRoot } from './components/app/AppRoot';
import KeyboardShortcutsHelp from './components/shared/KeyboardShortcutsHelp';
import { CapacitorSafeAreaProvider } from './components/mobile/CapacitorSafeAreaProvider';
import { ReactQueryProvider } from './components/app/ReactQueryProvider';
import { DebugErrorBoundary } from './components/app/DebugErrorBoundary';
// Import Capacitor utilities
import { initializeCapacitor } from './capacitor-utils';
// Import for offline support
import { initializeOfflineDB } from './lib/offline-db';
import { logger } from './utils/logger';

function AppWrapper() {
  // Create a global state for showing keyboard shortcuts
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  // Create global function to toggle shortcuts help
  // We'll export this through the window object for components to access
  const toggleShortcutsHelp = () => {
    setShowShortcutsHelp(prev => !prev);
  };

  // Initialize Capacitor if we're in a native app
  useEffect(() => {
    // Simple initialization of Capacitor (handles splash screen, status bar, back button, and keyboard)
    initializeCapacitor().catch(error => {
      console.error('Error initializing Capacitor:', error);
    });
  }, []);

  // Initialize offline database in background
  useEffect(() => {
    const initOfflineSupport = async () => {
      try {
        const initialized = await initializeOfflineDB();
        logger.store('Offline database initialization completed', { success: initialized });
      } catch (error) {
        logger.error('Failed to initialize offline database:', error);
      }
    };

    initOfflineSupport();
  }, []);

  // Expose the toggle function globally so components can access it
  useEffect(() => {
    // Define on window object
    window.toggleKeyboardShortcutsHelp = toggleShortcutsHelp;

    // Clean up on unmount
    return () => {
      if (window.toggleKeyboardShortcutsHelp === toggleShortcutsHelp) {
        delete window.toggleKeyboardShortcutsHelp;
      }
    };
  }, []);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ViewportProvider>
        {/* 
          IMPORTANT: Provider order matters!
          ReactQueryProvider must be placed before ConnectionStateProvider
          because ConnectionStateProvider uses React Query hooks internally
        */}
        <ReactQueryProvider>
          <DebugErrorBoundary>
            <ConnectionStateProvider>
              <KeyboardShortcutsProvider>
                <CapacitorSafeAreaProvider>
                  <AppRoot />
                  <KeyboardShortcutsHelp
                    isOpen={showShortcutsHelp}
                    onClose={() => setShowShortcutsHelp(false)}
                  />
                </CapacitorSafeAreaProvider>
              </KeyboardShortcutsProvider>
            </ConnectionStateProvider>
          </DebugErrorBoundary>
        </ReactQueryProvider>
      </ViewportProvider>
    </Router>
  );
}

export default AppWrapper;
